import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardPaste, ExternalLink, Plus, Users } from "lucide-react";
import { listingsApi, type ListingListParams, type ListingSummaryDto } from "@/api/buyers";
import { ListFilterBar } from "@/components/layout/ListFilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  ColumnChooser,
  EmptyState,
  FilterChips,
  ListToolbar,
  MissingValue,
  Skeleton,
  SortSelect,
  SubTable,
  SUB_TABLE_CHROME_THRESHOLD,
  Timestamp,
  Tooltip,
  type ActiveFilterChip,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import {
  listingDisplayName,
  listingShortName,
  shortenListingName,
} from "@/lib/listingDisplay";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useListFilters } from "@/hooks/useListFilters";
import { anyFilterActive, LISTING_SORTS, resolveSort, type ListingCardFilter } from "./buyerFilters";
import { BuyerPager } from "./BuyerPager";
import {
  DEFAULT_LISTING_VIEW,
  LISTING_ATTENTION_REASONS,
  LISTING_LIST_FILTERS,
  LISTING_VIEWS,
} from "./listingFilters";
import { ListingAgentDirectoryModal } from "./ListingAgentDirectory";
import { ListingEditModal } from "./ListingEditModal";
import { PasteToCreateListingModal } from "./PasteToCreateListingModal";
import { QueryErrorState } from "./queryStates";
import {
  AGENT_CHOOSER_COLUMNS,
  DEFAULT_OFF_AGENT_COLUMNS,
  listingAgentColumns,
  useColumnsWithDefaultOff,
} from "./listingAgentColumn";
import { safeHref } from "@/design-system/lib/safeHref";

/**
 * Listings — the businesses for sale that buyers are presented to.
 *
 * <b>Shared records, deliberately unscoped.</b> Two specialists working the
 * same listing must both see and maintain it, so there are no ownership
 * filters here and no "mine" toggle: scoping a shared catalog per specialist
 * would defeat the point of sharing it.
 */
/** The server's page size — and what tells `SubTable` it can never hold enough
 *  rows to earn chrome of its own. See the note at the table. */
const PAGE_SIZE = 25;

/**
 * The list request as the server actually accepts it.
 *
 * <p>🔴 <c>sortBy</c>/<c>sortDirection</c> live on <c>PagedQuery</c> and are read
 * by <c>ListingService.ApplySort</c>; the shared client mirror never declared
 * them, which is the only reason this list has had no sort control. They belong
 * on <c>ListingListParams</c> — declared here because <c>api/buyers.ts</c> is
 * outside this change's scope.</p>
 */
type ListingListRequest = ListingListParams & {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
};

/** Per-user preference keys (Q7). */
const VIEW_PREFERENCE = "listings-list-view";
const COLUMNS_PREFERENCE = "listings-list-columns";

/** The columns the reader may hide. The identity column is locked. */
const CHOOSABLE_COLUMNS = [
  { key: "listing", label: "Listing", locked: true },
  { key: "location", label: "Location" },
  { key: "asking", label: "Asking" },
  { key: "cashflow", label: "Cash flow" },
  // Addendum B (B1): the agent (key unchanged — it is in saved choices) and
  // two contact columns OFF by default.
  ...AGENT_CHOOSER_COLUMNS,
  { key: "submissions", label: "Submissions" },
  { key: "added", label: "Added" },
];

export function ListingsListPage() {
  const filters = useListFilters(LISTING_LIST_FILTERS);
  const { values, page } = filters;
  // Self-gating via `useOptionalAuth` — the ConvertToBorrowerButton precedent.
  // No auth context resolved = not permitted, which fails closed rather than
  // offering a write the server will refuse.
  const auth = useOptionalAuth();
  const canManage = Boolean(auth?.hasPermission("Listings.Manage"));

  const [creating, setCreating] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);

  // Q7 — the last view and the hidden columns, per user.
  const viewPref = useUserPreference<string>(VIEW_PREFERENCE, "");
  const { hiddenKeys: hiddenColumnKeys, onChange: onColumnsChange } = useColumnsWithDefaultOff(
    COLUMNS_PREFERENCE,
    DEFAULT_OFF_AGENT_COLUMNS,
  );

  // URL choice → persisted preference → the default. `view` is never written
  // to the URL at its default, so a bare `/listings` resolves through the
  // preference and a shared `?view=` link opens the same rows for anyone.
  const activeView =
    values.view ||
    (LISTING_VIEWS.some((v) => v.key === viewPref.value) ? viewPref.value : "") ||
    DEFAULT_LISTING_VIEW;
  const view = LISTING_VIEWS.find((v) => v.key === activeView) ?? LISTING_VIEWS[0];

  const selectView = (key: string) => {
    filters.set("view", key);
    viewPref.set(key);
  };

  // The attention reason in force. Inside the "Needs attention" view an
  // unchosen reason means the first one — see `LISTING_ATTENTION_REASONS` for
  // why the view cannot send the union; outside it a reason arrives only from
  // a deep link (`?card=`) and is honoured as it always was.
  const attentionKey: ListingCardFilter =
    values.card || (activeView === "needs-attention" ? LISTING_ATTENTION_REASONS[0].key : "");
  const attention = LISTING_ATTENTION_REASONS.find((r) => r.key === attentionKey);

  // Effective sort: an explicit choice on the control wins; otherwise the view
  // says how it opens; otherwise the descriptor's default.
  const sortToken =
    values.sort !== LISTING_SORTS[0].value ? values.sort : (view.sort ?? LISTING_SORTS[0].value);

  const params = useMemo<ListingListRequest>(
    () => ({
      // The view's own params sit UNDER everything explicit.
      ...view.params,
      page,
      pageSize: PAGE_SIZE,
      ...resolveSort(LISTING_SORTS, sortToken),
      q: values.q || undefined,
      state: values.state || undefined,
      industry: values.industry || undefined,
      marketplaceName: values.marketplace || undefined,
      hasSubmissions: values.inPlay || view.params.hasSubmissions ? true : undefined,
      engagement: values.phase || undefined,
      // ONE attention reason at a time, riding the SAME server-side expression
      // its count was computed with, so a chip and this table cannot report
      // different populations.
      ...(attention?.params ?? {}),
    }),
    [page, values, view, sortToken, attention],
  );

  const list = useQuery({
    queryKey: listingKeys.list(params),
    queryFn: () => listingsApi.list(params),
    // Wait for the persisted view: firing with the fallback and again with the
    // preference would be the double-fetch class, one render apart.
    enabled: !viewPref.isLoading,
  });

  // The SAME params as the table, minus paging and minus the pill filter
  // itself — otherwise clicking a pill collapses every other pill to zero and
  // there is no way back except the browser's Back button. The engagement rung
  // is dropped SERVER-side for the same reason, so the stage bar keeps showing
  // every rung while one of them is selected.
  const statsParams = useMemo<ListingListRequest>(
    () => ({
      ...params,
      page: 1,
      // Ordering cannot change a count — dropped so choosing a sort does not
      // re-key the stats query and refetch every tile for nothing.
      sortBy: undefined,
      sortDirection: undefined,
      noAgentEmailOnly: undefined,
      noAskingPriceOnly: undefined,
      noBuyerPresentedWithinDays: undefined,
    }),
    [params],
  );
  const stats = useQuery({
    queryKey: listingKeys.stats(statsParams),
    queryFn: () => listingsApi.stats(statsParams),
  });

  // 🔴 Memoised on the query result rather than written inline — `?? []` in a
  // render body allocates a fresh array every render while the query is in
  // flight (see `buyerSelection.ts` for what that cost once).
  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const total = list.data?.totalCount ?? 0;

  /**
   * §5 — search, sort and the footer arrive together at ten rows and not before.
   *
   * 🔴 Keyed on the whole result set plus "has the reader narrowed anything",
   * never on the rows currently rendered: a catalog filtered down to three rows
   * must keep the controls that got the reader there.
   */
  const hasChrome = anyFilterActive(filters) || total >= SUB_TABLE_CHROME_THRESHOLD;

  // Row 3 of the toolbar — everything narrowing the list that is not visible at
  // rest. Search and state are on screen; these are behind "More filters".
  const activeChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (values.industry)
      chips.push({ key: "industry", label: `Industry: ${values.industry}`, onRemove: () => filters.set("industry", "") });
    if (values.marketplace)
      chips.push({ key: "marketplace", label: `Marketplace: ${values.marketplace}`, onRemove: () => filters.set("marketplace", "") });
    return chips;
  }, [values.industry, values.marketplace, filters]);

  const attentionCount = (key: ListingCardFilter) =>
    key === "no-agent-email"
      ? stats.data?.noAgentEmail
      : key === "no-price"
        ? stats.data?.noAskingPrice
        : key === "no-buyer"
          ? stats.data?.noBuyerPresentedRecently
          : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title="Listings"
        description="Businesses for sale, their headline financials, and the agent representing each one."
        actions={
          canManage ? (
            <div className="flex items-center gap-space-2">
              {/*
                The agent registry lives HERE rather than on a listing, because
                an agent is shared across every listing they represent — hanging
                a cross-listing directory off one listing's page would imply a
                scope it does not have. The detail page keeps its own read-only
                block for the agent on that listing.
              */}
              <Button size="sm" variant="secondary" onClick={() => setAgentsOpen(true)}>
                <Users size={14} strokeWidth={1.5} aria-hidden /> Agents
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPasting(true)}>
                <ClipboardPaste size={14} strokeWidth={1.5} aria-hidden /> Paste to create
              </Button>
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus size={14} strokeWidth={1.5} aria-hidden /> New listing
              </Button>
            </div>
          ) : undefined
        }
      />

      {canManage && (
        <>
          <ListingEditModal open={creating} onClose={() => setCreating(false)} />
          <PasteToCreateListingModal open={pasting} onClose={() => setPasting(false)} />
          <ListingAgentDirectoryModal
            open={agentsOpen}
            onClose={() => setAgentsOpen(false)}
          />
        </>
      )}

      {/*
        §3 — one band: search | sort; views | state + the attention reasons |
        More filters | columns; the applied chips. Search and sort are §5-gated.

        🔴 THERE IS NO SCOPE TOGGLE HERE, AND THAT IS THE POINT. Listings are
        SHARED records: two specialists working the same listing must both see
        and maintain it, so there is no ownership filter to surface. The sibling
        lists carry a `ScopeToggle`; this one deliberately does not, and
        `BuyerListPages.test.tsx` pins the absence.

        🔴 `state` KEEPS ITS `<select>` AND STAYS IN THE PRIMARY ROW. §9.1
        preserves a dropdown for "a genuinely long, unbounded or free-text-
        searchable option list" — fifty-one US states is the long case — and it
        is the narrowing a reader reaches for first, so it earns its place at
        rest. `industry` and `marketplace` are free-text and go behind More.
      */}
      <ListToolbar
        data-testid="listings-toolbar"
        search={
          hasChrome
            ? {
                value: filters.inputs.q,
                onChange: (next) => filters.set("q", next),
                placeholder: "Search business, headline, industry, marketplace or agent (name, email, phone)…",
                label: "Search listings",
              }
            : undefined
        }
        sort={
          hasChrome ? (
            <SortSelect
              value={sortToken}
              onChange={(next) => filters.set("sort", next)}
              options={LISTING_SORTS}
              label="Sort listings by"
            />
          ) : undefined
        }
        views={{
          items: LISTING_VIEWS.map((v) => ({ key: v.key, label: v.label })),
          activeKey: activeView,
          onSelect: selectView,
          label: "Listing views",
        }}
        primary={
          <>
            <ListFilterBar filters={filters} className="mb-0" rows={[{ keys: ["state"] }]} />
            {activeView === "needs-attention" && (
              <FilterChips
                label="Attention reason"
                activeId={attentionKey}
                onChange={(key) => filters.set("card", key as ListingCardFilter)}
                chips={LISTING_ATTENTION_REASONS.map((r) => {
                  const count = attentionCount(r.key);
                  return { id: r.key, label: count == null ? r.label : `${r.label} (${count})` };
                })}
              />
            )}
          </>
        }
        moreFilters={{
          activeCount: activeChips.length,
          content: (
            <ListFilterBar
              filters={filters}
              className="mb-0"
              rows={[{ keys: ["industry", "marketplace"], className: "flex flex-col gap-space-2" }]}
            />
          ),
        }}
        activeChips={activeChips}
        onClearAllChips={() => {
          // ONE write — `set` builds its next params from THIS render's, so a
          // chain of sets would have the last overwrite the rest.
          filters.reset();
        }}
        actions={
          <ColumnChooser
            columns={CHOOSABLE_COLUMNS}
            hiddenKeys={hiddenColumnKeys}
            onChange={onColumnsChange}
            data-testid="listing-columns"
          />
        }
      />

      {/* The engagement, volume and reach blocks moved to the Reports page
          (WS-B4); a list is for working rows, not for reading the book. */}
      <p className="mb-space-3 text-caption text-gray-mid" data-testid="listings-reports-link">
        Engagement, listings added and reach are on{" "}
        <Link to="/buyers/reports?tab=listings" className="text-gold-shadow hover:underline">
          Reports
        </Link>
        .
      </p>

      {list.isLoading || viewPref.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <QueryErrorState
          what="the listings"
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No listings match"
          description={
            canManage
              ? "Widen the filters, or paste a listing to add one."
              : "Widen the filters to see more."
          }
        />
      ) : (
        /*
          `SubTable` picks the header variant from the surface it is mounted in
          (§4.3) and owns §5's threshold. `chromeThreshold={PAGE_SIZE}` puts that
          threshold permanently out of reach, because its search / sort / footer
          act on the ARRAY IT HOLDS — one server page of 25 — and would each
          claim a reach they do not have. The page owns all three, server-side.
        */
        <SubTable<ListingSummaryDto>
          caption="Listings"
          columns={LISTING_COLUMNS}
          rows={rows}
          rowKey={(l) => l.id}
          chromeThreshold={PAGE_SIZE}
          hiddenColumnKeys={hiddenColumnKeys}
          stickyHeader
          data-testid="listings-table"
        />
      )}

      <BuyerPager
        filters={filters}
        total={total}
        shown={rows.length}
        pageSize={PAGE_SIZE}
        hidden={!hasChrome}
      />
    </>
  );
}

/**
 * The row, as columns.
 *
 * 🔴 THE RICH CELLS SURVIVE. Two of them do real work and neither is decoration:
 * the identity cell pairs the shortened listing name with the outbound link to
 * the marketplace it came from, and the agent cell puts the firm under the
 * person. Adopting the shared chrome is about how the table looks and when it
 * grows controls — not a reason to flatten a row to match a simpler list.
 */
const LISTING_COLUMNS: SubTableColumn<ListingSummaryDto>[] = [
  {
    key: "listing",
    header: "Listing",
    render: (listing) => {
      // A blind listing legitimately has no business name. The ONE ladder
      // (business name → headline → the URL's host) is resolved server-side and
      // carried on the row; deriving it locally is the fallback for a payload
      // cached before the field existed.
      //
      // SHORT in the cell — this column already carries an industry/marketplace
      // caption under it and shares the row with five more — with the whole
      // marketplace title on hover.
      const full = listing.displayName?.trim() || listingDisplayName(listing);
      const title = shortenListingName(listing.displayName) ?? listingShortName(listing);
      return (
        <div className="min-w-0">
          <div className="flex items-center gap-space-2">
            {/*
              🔴 This used to be a native `title`, which §11.4 forbids outright:
              `title` never opens on keyboard focus, so a keyboard reader could
              reach the link and still be told nothing about the name it had cut
              short. `Tooltip` opens on hover AND on `:focus-visible`, is
              portaled out of the row's overflow container, and clones its child
              rather than wrapping it — so it adds no DOM node here.

              `aria-label` carries the full name as well, because a TOUCH user
              has no hover and may never focus the link. The visible text stays
              short, so nothing about the column's width changes.
            */}
            <Tooltip label={full}>
              <Link
                to={`/listings/${listing.id}`}
                className="text-gold-shadow hover:underline"
                aria-label={full === title ? undefined : full}
              >
                {title}
              </Link>
            </Tooltip>
            {listing.rawUrl && safeHref(listing.rawUrl) && (
              <a
                href={safeHref(listing.rawUrl) ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open the original listing"
                className="text-gray-mid hover:text-gold-shadow"
              >
                <ExternalLink size={13} strokeWidth={1.5} aria-hidden />
              </a>
            )}
          </div>
          <div className="text-caption text-gray-mid">
            {[listing.industry, listing.marketplaceName].filter(Boolean).join(" · ")}
          </div>
        </div>
      );
    },
  },
  {
    key: "location",
    header: "Location",
    render: (listing) => {
      const where = [listing.city, listing.state].filter(Boolean).join(", ");
      // §7 — a plain missing value. A listing with no location stated is a gap
      // in what the marketplace published, not a task with a next step.
      return where || <MissingValue />;
    },
  },
  {
    key: "asking",
    header: "Asking",
    align: "right",
    render: (listing) =>
      listing.askingPrice == null ? <MissingValue /> : formatCurrency(listing.askingPrice),
  },
  {
    key: "cashflow",
    header: "Cash flow",
    align: "right",
    render: (listing) =>
      listing.cashFlow == null ? <MissingValue /> : formatCurrency(listing.cashFlow),
  },
  ...listingAgentColumns<ListingSummaryDto>((l) => l.listingAgent, { withContactColumns: true }),
  {
    key: "submissions",
    header: "Submissions",
    align: "right",
    render: (listing) => listing.submissionCount,
  },
  {
    key: "added",
    header: "Added",
    render: (listing) => <Timestamp value={listing.createdAt} />,
  },
];
